import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';

const ProximaClaseWidget = () => {
  const navigate = useNavigate();
  const { user, API, getImageUrl } = useContext(AuthContext);
  const [proximoItem, setProximoItem] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && (user.rol === 'estudiante' || user.rol === 'diploma' || user.rol === 'graduado' || user.rol === 'pastor' || user.rol === 'profesor')) {
      loadProximoItem();
    } else {
      setLoading(false);
    }
  }, [user]);

  // Obtener hora actual en Uruguay (UTC-3)
  const getAhoraUruguay = () => {
    const ahora = new Date();
    // Convertir a UTC y luego restar 3 horas para Uruguay
    const utcTime = ahora.getTime() + (ahora.getTimezoneOffset() * 60000);
    return new Date(utcTime - (3 * 60 * 60 * 1000)); // UTC-3
  };

  // Parsear fecha asumiendo que siempre es hora de Uruguay
  const parseFechaUruguay = (fechaStr) => {
    if (!fechaStr) return null;
    
    // Si es string, parsearlo como hora de Uruguay (no UTC)
    if (typeof fechaStr === 'string') {
      // Remover Z si existe para evitar conversión UTC
      let cleanStr = fechaStr.replace('Z', '');
      // Si tiene +00:00, removerlo también
      cleanStr = cleanStr.replace(/[+-]\d{2}:\d{2}$/, '');
      
      // Parsear los componentes manualmente
      const match = cleanStr.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
      if (match) {
        const [_, year, month, day, hour, minute] = match;
        // Crear fecha en "hora de Uruguay" (los valores tal cual)
        return {
          year: parseInt(year),
          month: parseInt(month),
          day: parseInt(day),
          hour: parseInt(hour),
          minute: parseInt(minute)
        };
      }
    }
    return null;
  };

  // Actualizar countdown cada segundo
  useEffect(() => {
    if (!proximoItem?.fechaProxima) return;

    const interval = setInterval(() => {
      const ahoraUY = getAhoraUruguay();
      const fechaClase = parseFechaUruguay(proximoItem.fechaProxima);
      
      if (!fechaClase) {
        setCountdown({ dias: 0, horas: 0, minutos: 0, segundos: 0, esHoy: true });
        return;
      }
      
      // Crear fecha objetivo usando los valores de Uruguay
      const proximaFecha = new Date(
        fechaClase.year, 
        fechaClase.month - 1, 
        fechaClase.day, 
        fechaClase.hour, 
        fechaClase.minute, 
        0
      );
      
      // Calcular diferencia en milisegundos
      // ahoraUY ya está en "hora de Uruguay", proximaFecha también
      const diff = proximaFecha.getTime() - ahoraUY.getTime();

      if (diff <= 0) {
        setCountdown({ dias: 0, horas: 0, minutos: 0, segundos: 0, esHoy: true });
      } else {
        const dias = Math.floor(diff / (1000 * 60 * 60 * 24));
        const horas = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutos = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const segundos = Math.floor((diff % (1000 * 60)) / 1000);
        setCountdown({ dias, horas, minutos, segundos, esHoy: dias === 0 });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [proximoItem]);

  const loadProximoItem = async () => {
    try {
      const ahora = new Date();
      let todosLosItems = [];

      // Cargar solo eventos, tareas, pruebas y reuniones
      // (Las clases ya se muestran en MiClaseHoyWidget con imagen y Zoom)
      const requests = [
        axios.get(`${API}/eventos`).catch(() => ({ data: [] })),
        axios.get(`${API}/tareas`).catch(() => ({ data: [] })),
        axios.get(`${API}/pruebas-examenes`).catch(() => ({ data: { pruebas: [] } }))
      ];

      // Agregar reuniones si es profesor
      if (user?.rol === 'profesor') {
        requests.push(axios.get(`${API}/convocatorias-reunion`).catch(() => ({ data: { convocatorias: [] } })));
      }

      const results = await Promise.all(requests);

      // 1. EVENTOS
      const eventos = results[0].data || [];
      eventos.forEach(evento => {
        if (evento.fecha_inicio) {
          const fechaEvento = new Date(evento.fecha_inicio);
          if (fechaEvento > ahora) {
            todosLosItems.push({
              tipo: 'evento',
              titulo: evento.titulo,
              fechaProxima: evento.fecha_inicio,
              imagen_url: evento.imagen_url,
              categoria: evento.categoria,
              id: evento.id
            });
          }
        }
      });

      // 2. TAREAS (para estudiantes y profesores)
      const tareas = results[1].data || [];
      tareas.forEach(tarea => {
        // Solo tareas activas con fecha de entrega futura
        if (tarea.activa === false) return;
        if (tarea.fecha_entrega) {
          const fechaTarea = new Date(tarea.fecha_entrega);
          if (fechaTarea > ahora) {
            todosLosItems.push({
              tipo: 'tarea',
              titulo: tarea.titulo || 'Tarea',
              fechaProxima: tarea.fecha_entrega,
              imagen_url: null,
              categoria: 'estudiantes',
              id: tarea.id
            });
          }
        }
      });

      // 3. PRUEBAS Y EXÁMENES (para estudiantes)
      const pruebasData = results[2].data?.pruebas || results[2].data || [];
      pruebasData.forEach(prueba => {
        if (prueba.fecha_inicio || prueba.fecha_creacion) {
          const fechaPrueba = new Date(prueba.fecha_inicio || prueba.fecha_creacion);
          if (fechaPrueba > ahora) {
            todosLosItems.push({
              tipo: prueba.tipo === 'examen' ? 'examen' : 'prueba',
              titulo: prueba.titulo || 'Prueba',
              fechaProxima: prueba.fecha_inicio || prueba.fecha_creacion,
              imagen_url: prueba.imagen_url,
              categoria: 'estudiantes',
              id: prueba.id
            });
          }
        }
      });

      // 4. REUNIONES (solo profesores)
      if (user?.rol === 'profesor' && results[3]?.data?.convocatorias) {
        const reuniones = results[3].data.convocatorias || [];
        reuniones.forEach(reunion => {
          if (reunion.fecha) {
            const fechaReunion = new Date(reunion.fecha);
            if (fechaReunion > ahora) {
              todosLosItems.push({
                tipo: 'reunion',
                titulo: reunion.titulo || 'Reunión de Profesores',
                fechaProxima: reunion.fecha,
                imagen_url: null,
                categoria: 'profesores',
                id: reunion.id
              });
            }
          }
        });
      }

      // Ordenar por fecha y tomar el primero
      todosLosItems.sort((a, b) => new Date(a.fechaProxima) - new Date(b.fechaProxima));
      
      if (todosLosItems.length > 0) {
        setProximoItem(todosLosItems[0]);
      }
    } catch (error) {
      console.error('Error al cargar próximo item:', error);
    } finally {
      setLoading(false);
    }
  };

  // Encontrar la próxima fecha de una clase basada en sus días de semana
  // Para capacitaciones (profesores), mostrar hasta 4 días antes
  // IMPORTANTE: Siempre usa hora de Uruguay
  const encontrarProximaFechaClase = (clase, esCapacitacion = false) => {
    if (!clase.fecha_inicio || !clase.fecha_fin || !clase.dias_semana || clase.dias_semana.length === 0) {
      return null;
    }

    // Usar hora de Uruguay para todas las comparaciones
    const ahoraUY = getAhoraUruguay();
    
    // Parsear fechas de la clase (que vienen en hora de Uruguay)
    const fechaInicioData = parseFechaUruguay(clase.fecha_inicio);
    const fechaFinData = parseFechaUruguay(clase.fecha_fin);
    
    if (!fechaInicioData || !fechaFinData) return null;
    
    const fechaInicio = new Date(fechaInicioData.year, fechaInicioData.month - 1, fechaInicioData.day);
    const fechaFin = new Date(fechaFinData.year, fechaFinData.month - 1, fechaFinData.day, 23, 59, 59);

    // Si la clase aún no ha comenzado (comparar solo fechas)
    const ahoraFechaSolo = new Date(ahoraUY.getFullYear(), ahoraUY.getMonth(), ahoraUY.getDate());
    if (ahoraFechaSolo < fechaInicio) {
      // Retornar fecha de inicio con hora de la clase
      return new Date(
        fechaInicioData.year, 
        fechaInicioData.month - 1, 
        fechaInicioData.day, 
        fechaInicioData.hour, 
        fechaInicioData.minute
      );
    }

    // Si la clase ya terminó
    if (ahoraFechaSolo > fechaFin) {
      return null;
    }

    // Buscar el próximo día de clase
    const diasSemana = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
    const diaActual = ahoraUY.getDay();
    
    // Hora de la clase (ya en hora de Uruguay)
    const horaClase = { hora: fechaInicioData.hour, minutos: fechaInicioData.minute };
    
    const diasBusqueda = 7;

    for (let i = 0; i <= diasBusqueda; i++) {
      const diaCheck = (diaActual + i) % 7;
      const nombreDia = diasSemana[diaCheck];

      if (clase.dias_semana.some(d => d.toLowerCase() === nombreDia.toLowerCase())) {
        // Construir fecha próxima en hora de Uruguay
        const proximaFecha = new Date(ahoraUY);
        proximaFecha.setDate(proximaFecha.getDate() + i);
        proximaFecha.setHours(horaClase.hora, horaClase.minutos, 0, 0);

        // Si es hoy, verificar que no haya pasado la hora
        if (i === 0) {
          const ahoraMinutos = ahoraUY.getHours() * 60 + ahoraUY.getMinutes();
          const claseMinutos = horaClase.hora * 60 + horaClase.minutos;
          if (claseMinutos <= ahoraMinutos) {
            continue;
          }
        }

        // Verificar que no exceda la fecha de fin
        if (proximaFecha <= fechaFin) {
          // Para clases normales: solo mostrar si es HOY (mismo día)
          // Para capacitaciones: mostrar si está dentro de 4 días
          if (!esCapacitacion && i > 0) {
            continue;
          }
          if (esCapacitacion && i > 4) {
            continue;
          }
          return proximaFecha;
        }
      }
    }

    return null;
  };

  // Formatear fecha completa (usa hora de Uruguay)
  const formatearFechaCompleta = (fechaStr) => {
    if (!fechaStr) return '';
    const fechaData = parseFechaUruguay(fechaStr);
    if (!fechaData) return '';
    
    const fecha = new Date(fechaData.year, fechaData.month - 1, fechaData.day);
    const opciones = { weekday: 'long', day: 'numeric', month: 'long' };
    const resultado = fecha.toLocaleDateString('es-ES', opciones);
    return resultado.charAt(0).toUpperCase() + resultado.slice(1);
  };

  // No mostrar si no aplica al rol, está cargando o no hay item
  if (!user || !['estudiante', 'diploma', 'graduado', 'pastor', 'profesor'].includes(user.rol)) {
    return null;
  }

  if (loading || !proximoItem) {
    return null;
  }

  // Obtener la URL de la imagen
  const getImageSrc = () => {
    const imgUrl = proximoItem.imagen_url;
    if (!imgUrl) return null;
    if (imgUrl.startsWith('http')) return imgUrl;
    return getImageUrl ? getImageUrl(imgUrl) : imgUrl;
  };

  const imageSrc = getImageSrc();
  const esCapacitacion = proximoItem.tipo === 'capacitacion' || proximoItem.tipo === 'reunion' || proximoItem.categoria === 'profesores';

  // Determinar el label según el tipo
  const getLabel = () => {
    if (proximoItem.tipo === 'capacitacion') return '📚 Capacitación';
    if (proximoItem.tipo === 'reunion') return '👥 Reunión';
    if (proximoItem.tipo === 'clase') return '📖 Clase';
    if (proximoItem.tipo === 'tarea') return '📝 Tarea';
    if (proximoItem.tipo === 'prueba') return '✍️ Prueba';
    if (proximoItem.tipo === 'examen') return '📋 Examen';
    return '📅 Evento';
  };

  return (
    <div
      onClick={() => navigate('/app/calendario')}
      className={`group relative rounded-2xl cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-2xl overflow-hidden h-56 ${
        esCapacitacion 
          ? 'bg-gradient-to-br from-amber-500 via-orange-500 to-red-500'
          : 'bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500'
      }`}
      data-testid="proxima-clase-widget"
    >
      {/* Imagen de fondo si existe */}
      {imageSrc && (
        <div className="absolute inset-0">
          <img 
            src={imageSrc}
            alt={proximoItem.titulo}
            className="w-full h-full object-cover opacity-30 group-hover:opacity-40 transition-opacity duration-300"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div className={`absolute inset-0 bg-gradient-to-t ${
            esCapacitacion 
              ? 'from-amber-900/90 via-orange-800/60 to-transparent'
              : 'from-emerald-900/90 via-teal-800/60 to-transparent'
          }`} />
        </div>
      )}

      {/* Efecto de brillo animado */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />

      {/* Badge de tipo */}
      <div className="absolute top-3 right-3 z-20">
        <div className={`rounded-full px-2 py-1 text-xs font-bold shadow-lg ${
          esCapacitacion 
            ? 'bg-amber-100 text-amber-800'
            : 'bg-emerald-100 text-emerald-800'
        }`}>
          {getLabel()}
        </div>
      </div>

      {/* Contenido */}
      <div className="relative z-10 h-full flex flex-col p-5 text-white">
        {/* Encabezado */}
        <div className="flex items-center gap-2 mb-2">
          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
            <i className="fas fa-clock text-xl" />
          </div>
          <div>
            <p className="text-xs font-medium opacity-80">Próximo</p>
            <p className="text-sm font-bold">{formatearFechaCompleta(proximoItem.fechaProxima)}</p>
          </div>
        </div>

        {/* Título */}
        <h3 className="font-bold text-base leading-tight line-clamp-2 drop-shadow-lg mb-2">
          {proximoItem.titulo}
        </h3>

        {/* Countdown */}
        {countdown && (
          <div className="flex-1 flex items-center justify-center">
            {countdown.esHoy && countdown.horas === 0 && countdown.minutos === 0 ? (
              <div className="text-center">
                <div className="text-2xl font-black">
                  <i className="fas fa-calendar-check mr-2" />
                  ¡HOY!
                </div>
              </div>
            ) : countdown.dias === 0 ? (
              <div className="flex gap-2 text-center">
                <div className="bg-white/20 rounded-lg px-3 py-2 backdrop-blur-sm">
                  <div className="text-2xl font-black">{String(countdown.horas).padStart(2, '0')}</div>
                  <div className="text-xs opacity-80">hrs</div>
                </div>
                <div className="bg-white/20 rounded-lg px-3 py-2 backdrop-blur-sm">
                  <div className="text-2xl font-black">{String(countdown.minutos).padStart(2, '0')}</div>
                  <div className="text-xs opacity-80">min</div>
                </div>
                <div className="bg-white/20 rounded-lg px-3 py-2 backdrop-blur-sm">
                  <div className="text-2xl font-black">{String(countdown.segundos).padStart(2, '0')}</div>
                  <div className="text-xs opacity-80">seg</div>
                </div>
              </div>
            ) : (
              <div className="flex gap-2 text-center">
                <div className="bg-white/20 rounded-lg px-3 py-2 backdrop-blur-sm">
                  <div className="text-2xl font-black">{countdown.dias}</div>
                  <div className="text-xs opacity-80">días</div>
                </div>
                <div className="bg-white/20 rounded-lg px-3 py-2 backdrop-blur-sm">
                  <div className="text-2xl font-black">{String(countdown.horas).padStart(2, '0')}</div>
                  <div className="text-xs opacity-80">hrs</div>
                </div>
                <div className="bg-white/20 rounded-lg px-3 py-2 backdrop-blur-sm">
                  <div className="text-2xl font-black">{String(countdown.minutos).padStart(2, '0')}</div>
                  <div className="text-xs opacity-80">min</div>
                </div>
                <div className="bg-white/20 rounded-lg px-3 py-2 backdrop-blur-sm">
                  <div className="text-2xl font-black">{String(countdown.segundos).padStart(2, '0')}</div>
                  <div className="text-xs opacity-80">seg</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="mt-auto pt-3 border-t border-white/20 flex items-center justify-between">
          <span className="text-xs opacity-90">
            <i className="fas fa-calendar mr-1" />
            Calendario
          </span>
          <span className="text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <i className="fas fa-arrow-right mr-1" />
            Ver más
          </span>
        </div>
      </div>

      {/* Decoración */}
      {!imageSrc && (
        <>
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12" />
        </>
      )}
    </div>
  );
};

export default ProximaClaseWidget;
